import { useEffect, useState } from 'react';
import { ClpSdk, Fetcher, FetcherApi, JsonFetcher } from '@canvas-logic/clp-sdk';

import { uiFallBack, view3dFallBack } from '@/constants';
import { Sdk } from '@/types';
import { useConfiguratorRoute } from '@/hooks/useConfiguratorRoute';

import { Configurator } from './Configurator';
import { ConfiguratorContext, ConfiguratorSettings } from './ConfiguratorContext';

import { useLanguageSwitcher } from '@/hooks/useLanguageSwitcher';
import { IConfiguratorCatalogProductApiOptions } from '@canvas-logic/clp-api-types';

export enum Source {
  Local,
  Bucket,
  Backend,
}

interface IProps {
  source: Source;
  composite: boolean;
}

export const ProductConfigurator = ({ source, composite }: IProps) => {
  useLanguageSwitcher();
  const { draft, catalogId, projectId, productId, link, configurationId, hideConfigurationControls, hide3d } =
    useConfiguratorRoute();
  const params: IConfiguratorCatalogProductApiOptions = {
    version: draft ? 'draft' : 'active',
    catalogId,
    projectId,
    productId,
    configurationId,
  };
  const [state, setState] = useState<ConfiguratorSettings | null>(null);
  const [loadConfigurationError, setLoadConfigurationError] = useState<string | null>(null);

  useEffect(() => {
    // fetch('https://storage.googleapis.com/clp-basic-configurator/assets/acer-kiosk/configs/ui_specification.json')
    //   .then((res) => res.json())
    //   .then((a) => console.log('bucket', a));

    let fetcherBaseUrl = '';
    let assetsBaseUrl = '';
    let fetcher: FetcherApi;
    switch (source) {
      case Source.Local:
        fetcherBaseUrl = `./assets/${projectId}`;
        assetsBaseUrl = fetcherBaseUrl;
        fetcher = new JsonFetcher(fetcherBaseUrl);
        break;
      case Source.Bucket:
        fetcherBaseUrl = `https://storage.googleapis.com/${projectId}`;
        assetsBaseUrl = fetcherBaseUrl;
        fetcher = new JsonFetcher(fetcherBaseUrl);
        break;
      case Source.Backend:
        fetcherBaseUrl = window.APP_CONFIG.SERVER_URL;
        assetsBaseUrl = '';
        fetcher = new Fetcher(fetcherBaseUrl);
        break;
      default:
        throw new Error(`Invalid source ${source}`);
    }

    const catalogSdk = new ClpSdk(fetcher)
      .useCatalog(catalogId)
      .useApiOptions({ version: draft ? 'draft' : 'active', link, configurationId, projectId })
      .build();
    const productIdNumber = productId;

    const apiPromise = composite
      ? catalogSdk.loadCompositeProduct(productIdNumber)
      : catalogSdk.loadProduct(productIdNumber);

    const view3dPromise = (
      composite ? fetcher.getCompositeView3dSpecification(params) : fetcher.getProductView3dSpecification(params)
    ).catch((error) => {
      console.error(`Cannot load view3d.json for product ${productId}. Using fallback value`);
      console.error(error);
      return view3dFallBack;
    });

    const uiPromise = (
      composite ? fetcher.getCompositeUiSpecification(params) : fetcher.getProductUiSpecification(params)
    ).catch((error) => {
      console.error(`Cannot load ui.json for product ${productId}. Using fallback value`);
      return uiFallBack;
    });

    Promise.all([apiPromise, view3dPromise, uiPromise])
      .then(([api, view3d, configuratorUI]) => {
        setLoadConfigurationError(null);
        setState({
          params,
          sdk: { composite, api } as Sdk,
          projectId,
          draft,
          productId: productIdNumber,
          configuratorUI: configuratorUI,
          view3d: view3d,
          hideConfigurationControls,
          fetcher,
          without3d: hide3d,
        });
      })
      .catch((error) => {
        setLoadConfigurationError(error.message);
        console.error(error);
        setState(null);
      });
  }, [link]);

  return (
    <ConfiguratorContext.Provider value={state}>
      <Configurator loadConfigurationError={loadConfigurationError} />
    </ConfiguratorContext.Provider>
  );
};
